import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HttpClientError, IDefaultResponseMessage, IQuerySettings, QueryKeys, getQuerySettings } from '@dw/models/shared';
import { useAuth, useHttpClient, useSnackbar } from '@dw/hooks';
import { USER_ROLE } from "@dw/models/danceSchoolUser";
import { IBulkSyncIssuesActionResponse, ISyncIssue, SYNC_ISSUE_STATUS, SYNC_PROVIDER } from "@dw/models/syncIssue";

const BASE_URL = '/v1/sync-issues';

export const useGetSyncIssues = (provider?: SYNC_PROVIDER, status?: 'all' | SYNC_ISSUE_STATUS, settings?: IQuerySettings<ISyncIssue[]>) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<ISyncIssue[], HttpClientError>({
    queryKey: [QueryKeys.SyncIssues, provider ?? 'all', status ?? 'failed'],
    queryFn: () => {
      return GET<ISyncIssue[]>(BASE_URL, { provider, status });
    },
    ...querySettings
  });
}

/**
 * Number of failed Xero syncs — used to badge the Settings tab and the Integrations chip. Only
 * fetches when the user can manage the school (owner/admin) and Xero is connected; otherwise 0.
 */
export const useFailedXeroSyncCount = (): number => {
  const { danceSchool, userHasRole } = useAuth();

  const canManage = userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN);
  const xeroConnected = danceSchool?.xeroConnectionStatus?.connected ?? false;

  const { data } = useGetSyncIssues(SYNC_PROVIDER.XERO, SYNC_ISSUE_STATUS.FAILED, {
    enabled: canManage && xeroConnected
  });

  return data?.length ?? 0;
};

export const useRetrySyncIssueMutation = (settings?: IQuerySettings<IDefaultResponseMessage, string>) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const retrySyncIssue = async (id: string) => {
    return POST<IDefaultResponseMessage>(`${BASE_URL}/${id}/retry`);
  }

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, string>({
    mutationFn: retrySyncIssue,
    ...querySettings,
    onSuccess: (response, input) => {
      showSnackbar('Retry queued — the issue will move out of Failed and return if it fails again.');
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SyncIssues]
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}

export const useDismissSyncIssueMutation = (settings?: IQuerySettings<IDefaultResponseMessage, string>) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const dismissSyncIssue = async (id: string) => {
    return POST<IDefaultResponseMessage>(`${BASE_URL}/${id}/dismiss`);
  }

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, string>({
    mutationFn: dismissSyncIssue,
    ...querySettings,
    onSuccess: (response, input) => {
      showSnackbar('Sync issue dismissed.');
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SyncIssues]
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}

export const useRetryAllSyncIssuesMutation = (settings?: IQuerySettings<IBulkSyncIssuesActionResponse, SYNC_PROVIDER>) => {
  const { POST, buildUrl } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const retryAllSyncIssues = async (provider: SYNC_PROVIDER) => {
    return POST<IBulkSyncIssuesActionResponse>(buildUrl(`${BASE_URL}/retry-all`, { provider }));
  }

  const querySettings = getQuerySettings(settings);
  return useMutation<IBulkSyncIssuesActionResponse, HttpClientError, SYNC_PROVIDER>({
    mutationFn: retryAllSyncIssues,
    ...querySettings,
    onSuccess: (response, input) => {
      const { processedCount, skippedCount } = response;
      showSnackbar(`Queued ${processedCount} ${processedCount === 1 ? 'retry' : 'retries'}${skippedCount > 0 ? ` (${skippedCount} skipped)` : ''}.`);
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SyncIssues]
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}

export const useDismissAllSyncIssuesMutation = (settings?: IQuerySettings<IBulkSyncIssuesActionResponse, SYNC_PROVIDER>) => {
  const { POST, buildUrl } = useHttpClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const dismissAllSyncIssues = async (provider: SYNC_PROVIDER) => {
    return POST<IBulkSyncIssuesActionResponse>(buildUrl(`${BASE_URL}/dismiss-all`, { provider }));
  }

  const querySettings = getQuerySettings(settings);
  return useMutation<IBulkSyncIssuesActionResponse, HttpClientError, SYNC_PROVIDER>({
    mutationFn: dismissAllSyncIssues,
    ...querySettings,
    onSuccess: (response, input) => {
      const { processedCount, skippedCount } = response;
      showSnackbar(`Dismissed ${processedCount} sync ${processedCount === 1 ? 'issue' : 'issues'}${skippedCount > 0 ? ` (${skippedCount} skipped)` : ''}.`);
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SyncIssues]
      });
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}
